/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

function Customer() {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const formRef = useRef(null);
    const editFormRef = useRef(null);

    // Form state
    const [newRecord, setNewRecord] = useState({
        name: '', email: '', phone: '', alternate_phone: '',
        gender: '', dob: '', address: '', status: 'active'
    });
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Table state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // ─── Filter & Sort ───────────────────────────────────────────────────────────

    const filtered = customers.filter(c =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = React.useMemo(() => {
        let sortable = [...filtered];
        if (sortConfig.key) {
            sortable.sort((a, b) => {
                let aVal = a[sortConfig.key] ?? '';
                let bVal = b[sortConfig.key] ?? '';
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortable;
    }, [filtered, sortConfig]);

    const totalItems = sorted.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = sorted.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
        return '';
    };

    useEffect(() => { setCurrentPage(1); }, [searchTerm]);

    // ─── API ─────────────────────────────────────────────────────────────────────

    const fetchCustomers = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/customer');
            setCustomers(res.data.data || res.data || []);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to fetch customers' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCustomers(); }, []);

    // ─── Handlers ────────────────────────────────────────────────────────────────

    const handleChange = (e) => {
        setNewRecord(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const resetForm = () => {
        setNewRecord({ name: '', email: '', phone: '', alternate_phone: '', gender: '', dob: '', address: '', status: 'active' });
        setSelectedCustomer(null);
        if (formRef.current) formRef.current.classList.remove('was-validated');
        if (editFormRef.current) editFormRef.current.classList.remove('was-validated');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formRef.current.checkValidity()) {
            formRef.current.classList.add('was-validated');
            return;
        }
        try {
            const user_id = localStorage.getItem('user_id');
            await axiosInstance.post('/api/customer', { ...newRecord, isactive: true, created_by: user_id, updated_by: user_id });
            Swal.fire({ icon: 'success', title: 'Created!', text: 'Customer added successfully.', timer: 1500, showConfirmButton: false });
            window.$('#add_customer').modal('hide');
            resetForm();
            fetchCustomers();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create customer' });
        }
    };

    const handleEditClick = (customer) => {
        setSelectedCustomer(customer);
        setNewRecord({
            name: customer.name || '',
            email: customer.email || '',
            phone: customer.phone || '',
            alternate_phone: customer.alternate_phone || '',
            gender: customer.gender || '',
            dob: customer.dob ? customer.dob.split('T')[0] : '',
            address: customer.address || '',
            status: customer.status || 'active',
        });
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedCustomer) return;
        try {
            const user_id = localStorage.getItem('user_id');
            await axiosInstance.put(`/api/customer/${selectedCustomer.id}`, { ...newRecord, updated_by: user_id });
            Swal.fire({ icon: 'success', title: 'Updated!', text: 'Customer updated successfully.', timer: 1500, showConfirmButton: false });
            window.$('#edit_customer').modal('hide');
            resetForm();
            fetchCustomers();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update customer' });
        }
    };

    const handleDelete = async (customer) => {
        const result = await Swal.fire({
            title: 'Delete Customer?',
            text: `Are you sure you want to delete "${customer.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete!'
        });
        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/customer/${customer.id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                fetchCustomers();
            } catch {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete customer' });
            }
        }
    };

    // ─── Pagination ───────────────────────────────────────────────────────────────

    const Pagination = () => {
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);
        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalItems)} of {totalItems} entries
                    {filtered.length !== customers.length && ` (filtered from ${customers.length} total)`}
                </div>
                <nav>
                    <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(1)}><i className="fa fa-angle-double-left"></i></button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                        </li>
                        {pageNumbers.map(n => (
                            <li key={n} className={`page-item ${n === currentPage ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(n)}>{n}</button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(totalPages)}><i className="fa fa-angle-double-right"></i></button>
                        </li>
                    </ul>
                </nav>
            </div>
        );
    };

    const STATUS_COLORS = { active: 'bg-success', inactive: 'bg-danger', blocked: 'bg-warning' };

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Customers</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <div className="dropdown dropdown-action">
                                            <a href="#" className="btn-filters" data-bs-toggle="dropdown" aria-expanded="false">
                                                <span><i className="fe fe-download"></i></span>
                                            </a>
                                            <div className="dropdown-menu dropdown-menu-end">
                                                <ul className="d-block">
                                                    <li><a className="d-flex align-items-center download-item" href="javascript:void(0);"><i className="far fa-file-pdf me-2"></i>PDF</a></li>
                                                    <li><a className="d-flex align-items-center download-item" href="javascript:void(0);"><i className="far fa-file-text me-2"></i>CSV</a></li>
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                    <li>
                                        <a className="btn btn-primary" href="#" data-bs-toggle="modal" data-bs-target="#add_customer" onClick={resetForm}>
                                            <i className="fa fa-plus-circle me-2"></i>Add Customer
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select className="form-select form-select-sm" style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input type="text" className="form-control form-control-sm d-inline-block"
                                                style={{ width: '200px' }} placeholder="Search customers..."
                                                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Name{getSortIcon('name')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('phone')}>Phone{getSortIcon('phone')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>Email{getSortIcon('email')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('gender')}>Gender{getSortIcon('gender')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Status{getSortIcon('status')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>Created On{getSortIcon('createdAt')}</th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr><td colSpan="8" className="text-center"><div className="spinner-border" role="status"></div></td></tr>
                                                ) : currentItems.length > 0 ? (
                                                    currentItems.map((customer, index) => (
                                                        <tr key={customer.id}>
                                                            <td>{startIndex + index + 1}</td>
                                                            <td>
                                                                <span
                                                                    style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                                                    onClick={() => navigate(`/customer-details/${customer.id}`)}
                                                                    title="View Details"
                                                                >
                                                                    {customer.name}
                                                                </span>
                                                            </td>
                                                            <td>{customer.phone || 'N/A'}</td>
                                                            <td>
                                                                {customer.email
                                                                    ? <a href={`mailto:${customer.email}`} className="text-primary">{customer.email}</a>
                                                                    : 'N/A'}
                                                            </td>
                                                            <td>{customer.gender || 'N/A'}</td>
                                                            <td>
                                                                <span className={`badge ${STATUS_COLORS[customer.status] || 'bg-secondary'}`}>
                                                                    {customer.status || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>{customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A'}</td>
                                                            <td className="d-flex align-items-center">
                                                                <a className="btn-action-icon bg-primary-light me-2" style={{ cursor: 'pointer' }}
                                                                    onClick={() => navigate(`/customer-details/${customer.id}`)} title="View">
                                                                    <i className="fa fa-eye"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-success-light me-2" style={{ cursor: 'pointer' }}
                                                                    data-bs-toggle="modal" data-bs-target="#edit_customer"
                                                                    onClick={() => handleEditClick(customer)} title="Edit">
                                                                    <i className="fa fa-edit"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-danger-light" style={{ cursor: 'pointer' }}
                                                                    onClick={() => handleDelete(customer)} title="Delete">
                                                                    <i className="fa fa-trash-alt"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="8" className="text-center">No customers found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>

            {/* ── Add Customer Modal ── */}
            <div className="modal custom-modal fade" id="add_customer" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0"><i className="fa fa-user-plus me-2 text-primary"></i>Add Customer</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form ref={formRef} onSubmit={handleSubmit} noValidate>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Full Name <span className="text-danger">*</span></label>
                                            <input type="text" name="name" value={newRecord.name} onChange={handleChange}
                                                className="form-control" placeholder="Enter full name" required />
                                            <div className="invalid-feedback">Name is required.</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Phone <span className="text-danger">*</span></label>
                                            <input type="text" name="phone" value={newRecord.phone} onChange={handleChange}
                                                className="form-control" placeholder="Enter phone number" required />
                                            <div className="invalid-feedback">Phone is required.</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Email</label>
                                            <input type="email" name="email" value={newRecord.email} onChange={handleChange}
                                                className="form-control" placeholder="Enter email" />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Alternate Phone</label>
                                            <input type="text" name="alternate_phone" value={newRecord.alternate_phone} onChange={handleChange}
                                                className="form-control" placeholder="Alternate phone" />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="input-block mb-3">
                                            <label>Gender</label>
                                            <select name="gender" value={newRecord.gender} onChange={handleChange} className="form-control">
                                                <option value="">Select Gender</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="input-block mb-3">
                                            <label>Date of Birth</label>
                                            <input type="date" name="dob" value={newRecord.dob} onChange={handleChange} className="form-control" />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="input-block mb-3">
                                            <label>Status</label>
                                            <select name="status" value={newRecord.status} onChange={handleChange} className="form-control">
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                                <option value="blocked">Blocked</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Address</label>
                                            <textarea name="address" value={newRecord.address} onChange={handleChange}
                                                className="form-control" rows="2" placeholder="Enter address"></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">Cancel</button>
                                <button type="submit" className="btn btn-primary paid-continue-btn">Add Customer</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* ── Edit Customer Modal ── */}
            <div className="modal custom-modal fade" id="edit_customer" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0"><i className="fa fa-edit me-2 text-primary"></i>Edit Customer</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form ref={editFormRef} onSubmit={handleUpdate} noValidate>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Full Name <span className="text-danger">*</span></label>
                                            <input type="text" name="name" value={newRecord.name} onChange={handleChange}
                                                className="form-control" placeholder="Enter full name" required />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Phone <span className="text-danger">*</span></label>
                                            <input type="text" name="phone" value={newRecord.phone} onChange={handleChange}
                                                className="form-control" placeholder="Enter phone number" required />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Email</label>
                                            <input type="email" name="email" value={newRecord.email} onChange={handleChange}
                                                className="form-control" placeholder="Enter email" />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Alternate Phone</label>
                                            <input type="text" name="alternate_phone" value={newRecord.alternate_phone} onChange={handleChange}
                                                className="form-control" placeholder="Alternate phone" />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="input-block mb-3">
                                            <label>Gender</label>
                                            <select name="gender" value={newRecord.gender} onChange={handleChange} className="form-control">
                                                <option value="">Select Gender</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="input-block mb-3">
                                            <label>Date of Birth</label>
                                            <input type="date" name="dob" value={newRecord.dob} onChange={handleChange} className="form-control" />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="input-block mb-3">
                                            <label>Status</label>
                                            <select name="status" value={newRecord.status} onChange={handleChange} className="form-control">
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                                <option value="blocked">Blocked</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Address</label>
                                            <textarea name="address" value={newRecord.address} onChange={handleChange}
                                                className="form-control" rows="2" placeholder="Enter address"></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">Cancel</button>
                                <button type="submit" className="btn btn-primary paid-continue-btn">Update Customer</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Customer;